import { lazy, Suspense, useMemo, useState } from 'react';
import {
  extractOpeningAckBody,
  previewOpeningAckHtml,
  toStoredOpeningAckBody,
} from '../../lib/openingAckTemplate';
import { Modal } from '../ui/Modal';

const MemoBodyEditor = lazy(() =>
  import('../forms/MemoBodyEditor').then((m) => ({ default: m.MemoBodyEditor })),
);

type Props = {
  subject: string;
  onSubjectChange: (value: string) => void;
  bodyHtml: string;
  onBodyHtmlChange: (fullHtml: string) => void;
  disabled?: boolean;
  previewTicketNumber?: string;
  hideSubject?: boolean;
};

const PLACEHOLDERS = [
  { label: 'Ticket number', token: '{{ticket_number}}' },
  { label: 'Customer name', token: '{{requester_name}}' },
  { label: 'Subject', token: '{{subject}}' },
] as const;

export function OpeningAckTemplateEditor({
  subject,
  onSubjectChange,
  bodyHtml,
  onBodyHtmlChange,
  disabled,
  previewTicketNumber = 'BTH-ENQ-2608-0001',
  hideSubject = false,
}: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const editable = useMemo(() => extractOpeningAckBody(bodyHtml), [bodyHtml]);

  const preview = useMemo(
    () =>
      previewOpeningAckHtml(subject, bodyHtml, {
        ticket_number: previewTicketNumber,
        requester_name: 'Customer Name',
        subject: 'Sample enquiry subject',
      }),
    [subject, bodyHtml, previewTicketNumber],
  );

  function insertToken(token: string) {
    const base = editable.trim();
    const next = base
      ? `${base}<p style="margin:0;line-height:1.15;font-family:Calibri,'Segoe UI',sans-serif;font-size:11pt;">${token}</p>`
      : `<p style="margin:0;line-height:1.15;font-family:Calibri,'Segoe UI',sans-serif;font-size:11pt;">${token}</p>`;
    onBodyHtmlChange(toStoredOpeningAckBody(next));
  }

  return (
    <div className="opening-ack-editor">
      {!hideSubject ? (
        <label className="field">
          <span className="field-label">Subject</span>
          <input
            className="field-input"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            disabled={disabled}
            placeholder="[{{ticket_number}}] Your ticket has been created"
          />
        </label>
      ) : null}

      {!hideSubject ? (
        <div className="opening-ack-editor__placeholders">
          <span className="opening-ack-editor__placeholders-label">Insert into body:</span>
          {PLACEHOLDERS.map((p) => (
            <button
              key={p.token}
              type="button"
              className="btn-secondary btn-sm"
              disabled={disabled}
              onClick={() => insertToken(p.token)}
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="opening-ack-editor__compose">
        <div className="opening-ack-editor__compose-head">
          <span className="field-label">Email body</span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={disabled}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </button>
        </div>
        <p className="opening-ack-editor__hint">
          Uses the same editor as ticket Reply. Select an address, click <strong>Link</strong> to change the
          hyperlink, then Save template. Use <strong>Preview</strong> to see the full email.
        </p>
        <Suspense fallback={<p className="settings-empty">Loading editor…</p>}>
          <MemoBodyEditor
            value={editable}
            onChange={(html) => onBodyHtmlChange(extractOpeningAckBody(html))}
            disabled={disabled}
            emailFormat
            defaultEmailFont
            allowImages
            placeholder="Enter the acknowledgement email body"
          />
        </Suspense>
      </div>

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Email preview"
        extraWide
        nested
      >
        <div className="opening-ack-editor__preview-chrome">
          <p className="opening-ack-editor__preview-subject">
            <span>Subject</span> {preview.subject}
          </p>
          <div
            className="opening-ack-editor__preview-body ticket-mail"
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        </div>
      </Modal>
    </div>
  );
}
